import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule, FormControl } from '@angular/forms';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogRef } from '@angular/cdk/dialog';
import {
  ButtonComponent,
  DatePickerComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  FormFieldComponent,
} from '@akostm/orbit-ui';

export interface LockConversationData {
  lockNote: string;
  lockExpiresAt: Date;
}

@Component({
  selector: 'shared-lock-conversation',
  templateUrl: './lock-conversation.component.html',
  styleUrls: ['./lock-conversation.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DatePickerComponent,
    TranslateModule,
    DialogHeaderComponent,
    FormFieldComponent,
    ButtonComponent,
    DialogFooterComponent,
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LockConversationComponent {
  readonly #dialogRef = inject(DialogRef);
  readonly #translateService = inject(TranslateService);

  lockForm: FormGroup = new FormGroup({
    lockNote: new FormControl<string | null>(null),
    lockExpiresAt: new FormControl<Date | null>(null),
  });

  isSubmitting = signal(false);
  minimumDate = signal(new Date());

  // Translation parameters for the date picker
  todayLabel = computed(() => this.#translateService.instant('DATE_PICKER.TODAY'));
  clearLabel = computed(() => this.#translateService.instant('DATE_PICKER.CLEAR'));
  monthNames = computed(() => this.#translateService.instant('DATE_PICKER.MONTHS'));
  weekdayNames = computed(() => this.#translateService.instant('DATE_PICKER.WEEK_DAYS'));

  onCancel(): void {
    this.#dialogRef.close();
  }

  get lockNote() {
    return this.lockForm.get('lockNote') as FormControl<string>;
  }

  onSubmit(): void {
    if (this.lockForm.invalid) {
      this.lockForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    const lockData: LockConversationData = {
      lockNote: this.lockForm.value.lockNote,
      lockExpiresAt: this.lockForm.value.lockExpiresAt,
    };
    this.#dialogRef.close(lockData);
  }

  onCancelKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onCancel();
    }
  }
}
